
import { useState, useEffect, useMemo } from 'react';

// Generic imports
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

// Mui imports
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-light-svg-icons';

// Local imports
import Where from '../../Where.js';
import { getMultiplier } from '../../CalcWeight.js';

const DEFAULTS = {
    sea: ['20\' Dry', '40\' Dry', '40\' HC Dry'],
    air: ['Air freight'],
    land: ['Dry', 'Reefer', 'Tent', 'Mega', 'Tilt'],
}

function OpportunityContractAddRowsModal(props) {
    const { organization, open, transportationMethod, products, seaPorts, unlocode, onDone, onClose } = props;
    const [ sources, setSources ] = useState([]);
    const [ destinations, setDestinations ] = useState([]);
    const [ selectedProducts, setSelectedProducts ] = useState([]);
    const [ showMore, setShowMore ] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        setSources([]);
        setDestinations([]);
        setShowMore(false);
        setSelectedProducts(products.filter(p => !p.dangerous && DEFAULTS[transportationMethod].includes(p.type)).map(p => p.id));
    }, [open]);

    function onOk() {
        const _routes = (() => {
            if (sources.length === 0) return destinations.map(destination => ({ destination }));
            if (destinations.length === 0) return sources.map(source => ({ source }));
            return _.flatten(_.compact(sources.map(source => _.compact(destinations.map(destination => ({ source: source, destination: destination }))))));
        })();

        const _products = products.filter(p => selectedProducts.includes(p.id));
        onDone({ routes: _routes, selectedProducts: _products });
    }

    function onDeleteSource({ s }) {
        setSources(_.without(sources, s));
    }

    function onDeleteDestination({ s }) {
        setDestinations(_.without(destinations, s));
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth={'lg'} fullWidth>
            <DialogTitle>
                Add Contract Rows
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', alignItems: 'center', margin: '12px 0px', fontWeight: 800 }}>
                    Step 1 - Add Source and Destination
                </Box>
                <Box sx={{ margin: '6px', padding: '12px 18px', background: theme.palette.panel1, border: `1px solid ${theme.palette.border1}`, borderRadius: '6px' }}>
                    <Box sx={{ marginBottom: '12px' }}>
                        Enter a list of sources and a list of destinations, and we will add all combinations to the table (you can delete the ones you don't want later):
                    </Box>
                    <Where 
                        transportationMethod={transportationMethod}
                        errors={[]}
                        setSource={(v) => { setSources([...sources, v]) }}
                        setDestination={(v) => { setDestinations([...destinations, v]) }}
                        seaPorts={seaPorts}
                        unlocode={unlocode}
                    />
                    <Box sx={{ display: 'flex' }}>
                        <Box sx={{ flex: 1, padding: '3px' }}>
                            {sources.map(s => ( 
                                <Chip 
                                    key={s.text} 
                                    label={s.text} 
                                    sx={{ marginTop: '12px' }} 
                                    onDelete={() => onDeleteSource({s})} 
                                />
                            ))}
                        </Box>
                        <Box sx={{ flex: 1, padding: '3px' }}>
                            {destinations.map(s => ( 
                                <Chip 
                                    key={s.text} 
                                    label={s.text}
                                    sx={{ marginTop: '12px' }} 
                                    onDelete={() => onDeleteDestination({s})}
                                />
                            ))}
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '12px', fontWeight: 800 }}>
                    Step 2 - Which products would you like to offer?
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                    {(showMore ? products : products.filter(p => !p.dangerous && DEFAULTS[transportationMethod].includes(p.type))).map(p => {
                        const isUnsupported = p.type === 'LCL' || p.type === 'By Volume or Weight (sea)' || p.type === 'By Dimensions or Weight (sea)';
                        const chip = (
                            <Chip
                                sx={{ marginTop: '12px', marginRight: '6px', opacity: isUnsupported ? 0.5 : 1, cursor: isUnsupported ? 'not-allowed' : 'pointer' }}
                                key={p.id}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', background: selectedProducts.includes(p.id) ? '#605DE4' : theme.palette.panel1, borderRadius: '50%', color: 'white' }}>
                                            {selectedProducts.includes(p.id) && <FontAwesomeIcon icon={faCheck} />}
                                        </Box>
                                        <Box sx={{ marginLeft: '12px' }}>
                                            {p.type === 'Air Freight' ? `Chargeable Weight (1 kg)${p.dangerous ? ' (Dangerous)' : ''}` : (p.type === 'LCL' || p.type === 'By Volume or Weight (sea)') ? `Per ${getMultiplier({ product: p, transportationMethod, allProducts: [p], shorten: true, round: organization.airRoundChargeableWeight }).multiplier} ${getMultiplier({ product: p, transportationMethod, allProducts: [p], shorten: true, round: organization.airRoundChargeableWeight }).multiplierText}${p.dangerous ? ' (Dangerous)' : ''}` : `${p.type}${p.dangerous ? ' (Dangerous)' : ''}`}
                                        </Box>
                                    </Box>
                                }
                                onClick={isUnsupported ? undefined : () => setSelectedProducts(_.xor(selectedProducts, [p.id]))}
                            />
                        );
                        return isUnsupported
                            ? <Tooltip key={p.id} title='Coming Soon' arrow><span>{chip}</span></Tooltip>
                            : chip;
                    })}
                </Box>
                <Button
                    onClick={() => setShowMore(!showMore)}
                    variant='text'
                    color='primary'
                    sx={{ textTransform: 'none', marginTop: '12px' }}
                    disabled={showMore && _.some(selectedProducts, pId => {
                        const product = products.find(p => p.id === pId);
                        if (product.dangerous) return true;
                        if (!DEFAULTS[transportationMethod].includes(product.type)) return true;
                        return false;
                    })}
                >
                    {showMore ? 'Show less' : 'Show more...'}
                </Button>
            </DialogContent>
            <DialogActions sx={{ paddingBottom: '24px', paddingRight: '40px' }}>
                <Button size='small' color='secondary' onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    size='small' 
                    color='primary' 
                    variant='contained' 
                    onClick={onOk}
                >
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default OpportunityContractAddRowsModal;
